import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { CLIENT_STATUS_LABEL, clientStatus } from '../lib/clientStatus.js';
import { COLOR_SETS, PROJECT_TIERS } from '../lib/constants.js';
import { fmtTime } from '../lib/time.js';
import { sortOf } from '../lib/sessionState.js';
import { useSessionState } from '../lib/useSessionState.js';
import { Portal } from './controls/Portal.jsx';
import { Select } from './controls/Select.jsx';

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

function compareValues(left, right) {
  return typeof left === 'number' && typeof right === 'number'
    ? left - right
    : collator.compare(String(left), String(right));
}

// Shared sortable-table state for Table headers: tracks {key, direction},
// toggles direction on repeat clicks, and returns rows sorted via valueFn.
// flashToken bumps on every sortBy call, for callers that want to key rows
// with it and re-trigger the .flash-row highlight animation on re-sort.
// With a `persistKey` the sort is remembered for the tab session; pass the
// table's `headers` too, so a remembered sort on a column it no longer has
// falls back to `initialSort`.
export function useSortedRows(
  rows,
  valueFn,
  initialSort,
  persistKey = null,
  headers = null,
) {
  const [sort, setSort] = useSessionState(
    persistKey ? `sort.${persistKey}` : null,
    initialSort,
    sortOf(headers?.map((header) => header.sortKey).filter(Boolean)),
  );
  const [flashToken, setFlashToken] = useState(0);

  function sortBy(key) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
    setFlashToken((n) => n + 1);
  }

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const result = compareValues(
          valueFn(a, sort.key),
          valueFn(b, sort.key),
        );
        return sort.direction === 'asc' ? result : -result;
      }),
    [rows, sort, valueFn],
  );

  return [sorted, sort, sortBy, flashToken];
}

// Row key + className for a sortable table row that should replay the
// .flash-row highlight animation whenever flashToken changes (e.g. on sort).
export function flashRowProps(id, flashToken, className = '') {
  return {
    key: flashToken ? `${id}:${flashToken}` : id,
    className: flashToken ? `${className} flash-row`.trim() : className,
  };
}

// Parse a typed time as digits ("1830" -> 18:30, "9" -> 09:00, "930" -> 9:30)
// into decimal hours (0-24). Returns null for invalid input.
function parseTimeInput(value) {
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return null;
  const h = digits.length <= 2 ? Number(digits) : Number(digits.slice(0, -2));
  const m = digits.length <= 2 ? 0 : Number(digits.slice(-2));
  if (!Number.isFinite(h) || !Number.isFinite(m) || m > 59 || h > 24) {
    return null;
  }
  return Math.min(24, h + m / 60);
}

// A time-of-day field that always displays 24h "HH:MM" but becomes a
// digits-only input while editing (type "1830", commit to 18:30).
export function TimeField({ value, onChange }) {
  const [text, setText] = useState(() => fmtTime(value));
  const focusedRef = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!focusedRef.current) setText(fmtTime(value));
  }, [value]);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={text}
      onFocus={() => {
        focusedRef.current = true;
        setText(fmtTime(value).replace(':', ''));
        window.setTimeout(() => inputRef.current?.select(), 0);
      }}
      onChange={(event) => {
        const raw = event.target.value.replace(/\D/g, '').slice(0, 4);
        setText(raw);
        const parsed = parseTimeInput(raw);
        if (parsed != null) onChange(parsed);
      }}
      onBlur={() => {
        focusedRef.current = false;
        const parsed = parseTimeInput(text);
        setText(fmtTime(parsed != null ? parsed : value));
      }}
    />
  );
}

// Avatar hue is assigned by hashing the name, never chosen, so the same
// person is always the same colour (Steel Glass: four hues, three treatments).
const AVATAR_HUES = ['cyan', 'green', 'amber', 'rose'];

function avatarHue(user) {
  const key = user.name || user.email || '';
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_HUES[hash % AVATAR_HUES.length];
}

export function Avatar({ user, variant = 'soft', size = 32 }) {
  const initials =
    user.initials ||
    user.name
      ?.split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() ||
    '?';
  return (
    <span
      className={`avatar avatar--${variant} avatar--${size}`}
      data-hue={avatarHue(user)}
      // The tooltip is not an accessible name; without one, a reader hears
      // only the initials where no name sits beside the avatar.
      role="img"
      aria-label={user.name || initials}
      data-tooltip={user.name}
    >
      {initials}
    </span>
  );
}

export function Card({ title, className = '', children }) {
  return (
    <section className={className ? `card ${className}` : 'card'}>
      {title && <h2>{title}</h2>}
      {children}
    </section>
  );
}

export function Code({ project, big }) {
  const color = COLOR_SETS[project?.color] || COLOR_SETS.teal;
  return (
    <span
      className={`code ${big ? 'big' : ''}`}
      style={{ background: color.soft, color: color.text }}
    >
      {project?.code || '--'}
    </span>
  );
}

export function ProjectLabel({ project, muted }) {
  const color = COLOR_SETS[project?.color] || COLOR_SETS.teal;
  return (
    <span className={`project-label ${muted ? 'muted' : ''}`}>
      <span
        className="mini-code"
        style={{ background: color.soft, color: color.text }}
      >
        {project?.code || '--'}
      </span>
      <span className="truncate">{project?.name || 'Unknown project'}</span>
    </span>
  );
}

// Open modals form a stack so Escape only closes the topmost one (e.g. the
// delete-confirm opened on top of the entry modal).
const modalStack = [];

// Portal lives with the controls (their popovers need it too); re-exported
// here, where the dialogs and every existing caller import it from.
export { Portal };

export function Modal({ title, children, onClose }) {
  // onClose is usually an inline closure; keep it in a ref so the keydown
  // listener stays current without re-registering on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const entry = {};
    modalStack.push(entry);
    function onKeyDown(event) {
      if (event.key !== 'Escape') return;
      if (modalStack[modalStack.length - 1] !== entry) return;
      event.stopPropagation();
      onCloseRef.current?.();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const index = modalStack.indexOf(entry);
      if (index !== -1) modalStack.splice(index, 1);
    };
  }, []);

  return (
    <Portal>
      <div className="modal-backdrop" onMouseDown={onClose}>
        <div className="modal" onMouseDown={(event) => event.stopPropagation()}>
          <header>
            <h2>{title}</h2>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </header>
          {children}
        </div>
      </div>
    </Portal>
  );
}

export function ConfirmModal({
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  loading = false,
  error,
  onConfirm,
  onClose,
}) {
  return (
    <Modal title={title} onClose={loading ? undefined : onClose}>
      <div className="confirm-modal">
        {message && <p>{message}</p>}
        {error && <p className="field-error">{error}</p>}
        <div className="modal-actions">
          <span className="spacer" />
          <button
            type="button"
            className="secondary"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="danger"
            disabled={loading}
            onClick={onConfirm}
          >
            {loading ? 'Deleting...' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function ClientStatusBadge({ client }) {
  const status = clientStatus(client);
  return (
    <span
      className={`project-status-badge ${status === 'active' ? 'status-active' : 'status-completed'}`}
    >
      {CLIENT_STATUS_LABEL[status]}
    </span>
  );
}

// A question with several answers. `choices` render left → right; the last
// one is the primary action. Closing (Esc, ×, backdrop) answers `null`.
export function ChoiceModal({ title, message, choices, onChoose }) {
  return (
    <Modal title={title} onClose={() => onChoose(null)}>
      <div className="confirm-modal">
        {message && <p>{message}</p>}
        <div className="modal-actions">
          <span className="spacer" />
          {choices.map((choice) => (
            <button
              key={choice.value}
              type="button"
              className={choice.tone || 'secondary'}
              onClick={() => onChoose(choice.value)}
            >
              {choice.label}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// `ask({ title, message, choices })` opens a ChoiceModal and resolves with the
// chosen value (or null when dismissed), so a save handler can await the
// answer mid-flow. Render `modal` somewhere in the caller's tree.
export function useChoice() {
  const [pending, setPending] = useState(null);
  const ask = (question) =>
    new Promise((resolve) => setPending({ ...question, resolve }));
  const modal = pending && (
    <ChoiceModal
      {...pending}
      onChoose={(value) => {
        setPending(null);
        pending.resolve(value);
      }}
    />
  );
  return [ask, modal];
}

export function NavItem({ icon, label, active, muted, onClick }) {
  return (
    <button
      className={`nav-item ${active ? 'active' : ''} ${muted ? 'muted' : ''}`}
      onClick={onClick}
      // The collapsed rail hides the label, and an icon is not a name, so
      // the button carries one of its own.
      aria-label={label}
      data-tooltip={label}
    >
      {React.cloneElement(icon, { size: 20 })}
      <span>{label}</span>
    </button>
  );
}

export function NavSection({ title, children }) {
  return (
    <div className="nav-section">
      <div className="nav-title">{title}</div>
      {children}
    </div>
  );
}

export function Progress({ pct, danger, warn }) {
  return (
    <div className="progress">
      <span
        style={{ width: `${Math.min(100, pct)}%` }}
        className={danger ? 'danger' : warn ? 'warn' : ''}
      />
    </div>
  );
}

export function Stats({ cards }) {
  return (
    <div className={cards.length === 5 ? 'stats stats-five' : 'stats'}>
      {cards.map(([label, value, sub]) => (
        <Card key={label}>
          <span className="stat-label">{label}</span>
          <strong className="stat-value">{value}</strong>
          <span className="stat-sub">{sub}</span>
        </Card>
      ))}
    </div>
  );
}

// Tier picker for a project — a select over PROJECT_TIERS plus "Not set",
// which maps back to null.
const TIER_OPTIONS = [
  { value: '', label: 'Not set' },
  ...PROJECT_TIERS.map((tier) => ({
    value: String(tier),
    label: `Tier ${tier}`,
  })),
];

export function TierSelect({ value, onChange, ariaLabel = 'Tier' }) {
  return (
    <Select
      ariaLabel={ariaLabel}
      value={value == null ? '' : String(value)}
      onChange={(next) => onChange(next ? Number(next) : null)}
      options={TIER_OPTIONS}
    />
  );
}

export function Table({ headers, children, sort, onSort }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {headers.map((header) => {
              const item =
                typeof header === 'string' ? { label: header } : header;
              const active = item.sortKey && sort?.key === item.sortKey;
              return (
                <th key={item.label}>
                  {item.sortKey ? (
                    <button
                      className={`table-sort ${active ? 'active' : ''}`}
                      onClick={() => onSort?.(item.sortKey)}
                    >
                      <span>{item.label}</span>
                      <span className="sort-indicator">
                        {active ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    item.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
